import { observer } from 'mobx-react-lite'
import { links } from '@/utils/link'
import type {
  SupportingItemViewModel,
  TaxInfoGatheringViewModel,
} from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import {
  WizardNav,
  WizardNavList,
  WizardNavListItem,
  WizardNavScroller,
} from '@/components/Wizard/WizardNav'
import {
  TaxInfoGatheringMissingRequiredItemsNavigationModal,
  TaxInfoGatheringNavigationModal,
} from '@/features/jobs/details/tax-type/info-gathering/components/TaxInfoGatheringNavigationModal'
import { useCallback } from 'react'

interface TaxInfoGatheringNavContainerProps {
  vm: TaxInfoGatheringViewModel
  isInDialog?: boolean
}

/**
 * Info Gathering Nav Container
 */
export const TaxInfoGatheringNavContainer = observer(
  function TaxInfoGatheringNavContainer({
    vm,
    isInDialog,
  }: TaxInfoGatheringNavContainerProps) {
    return (
      <WizardNav>
        <WizardNavScroller spacingY="xl">
          <WizardNavList variant={isInDialog ? 'flat' : 'default'}>
            {vm.pageVm.allOrderedItems.map((itemVm) => (
              <TaxInfoGatheringWizardNavListItemContainer
                vm={vm}
                itemVm={itemVm}
                key={itemVm.item.id}
              />
            ))}
          </WizardNavList>
        </WizardNavScroller>
      </WizardNav>
    )
  },
)

interface TaxInfoGatheringWizardNavListItemContainerProps {
  itemVm: SupportingItemViewModel
  vm: TaxInfoGatheringViewModel
}

/**
 * Tax Info Gathering Wizard Nav List Item Container
 */
const TaxInfoGatheringWizardNavListItemContainer = observer(
  function TaxInfoGatheringWizardNavListItemContainer({
    vm,
    itemVm,
  }: TaxInfoGatheringWizardNavListItemContainerProps) {
    const isSkeleton = vm.isFetching

    const closeModals = useCallback(() => {
      // Ensure we always close the dialogs when navigating.
      vm.pageVm.navigationModalVm.close()
      vm.missingRequiredItemsModalVm.close()
    }, [vm])

    return (
      <WizardNavListItem
        replace
        isRequired={itemVm.item.required}
        isSkeleton={isSkeleton}
        label={formatSupportingItemLabel(itemVm)}
        to={links.taxJobInfoGathering(vm.job.id, itemVm.item.id)}
        isCompleted={itemVm.isCompleted}
        isActive={vm.pageVm.selectedItemVm === itemVm}
        onPress={closeModals}
        testId={getSupportingItemTestId(itemVm)}
      />
    )
  },
)

interface TaxInfoGatheringNavigationModalContainerProps {
  vm: TaxInfoGatheringViewModel
}

/**
 * Info Gathering Navigation Modal Container
 */
export const TaxInfoGatheringNavigationModalContainer = observer(
  function TaxInfoGatheringNavigationModalContainer({
    vm,
  }: TaxInfoGatheringNavigationModalContainerProps) {
    const { pageVm } = vm
    return (
      <TaxInfoGatheringNavigationModal
        isOpen={pageVm.navigationModalVm.showing}
        onClose={pageVm.navigationModalVm.close}
        onConnectWithProPress={vm.tryCompleteInfoGathering}
        completionPercentage={vm.pageVm.counts.completionPercentage}
        closeButtonTestId="info-gathering-nav-modal-close-button"
      >
        <TaxInfoGatheringNavContainer isInDialog vm={vm} />
      </TaxInfoGatheringNavigationModal>
    )
  },
)

interface TaxInfoGatheringMissingRequiredItemsModalContainerProps {
  vm: TaxInfoGatheringViewModel
}

/**
 * Tax Info Gathering Missing Required Items Modal Container
 */
export const TaxInfoGatheringMissingRequiredItemsModalContainer = observer(
  function TaxInfoGatheringMissingRequiredItemsModalContainer({
    vm,
  }: TaxInfoGatheringMissingRequiredItemsModalContainerProps) {
    return (
      <TaxInfoGatheringMissingRequiredItemsNavigationModal
        isOpen={vm.missingRequiredItemsModalVm.showing}
        onClose={vm.missingRequiredItemsModalVm.close}
      >
        <WizardNav>
          <WizardNavScroller spacingY="xl">
            <WizardNavList variant="flat">
              {vm.pageVm.allOrderedItems
                .filter((itemVm) => !itemVm.isCompleted && itemVm.item.required)
                .map((itemVm) => (
                  <TaxInfoGatheringWizardNavListItemContainer
                    vm={vm}
                    itemVm={itemVm}
                    key={itemVm.item.id}
                  />
                ))}
            </WizardNavList>
          </WizardNavScroller>
        </WizardNav>
      </TaxInfoGatheringMissingRequiredItemsNavigationModal>
    )
  },
)

function formatSupportingItemLabel(vm: SupportingItemViewModel): string {
  switch (vm.type) {
    case 'PersonalInfo':
      return 'Personal details'
    case 'SpousalInfo':
      return 'Spouse details'
    case 'DependentsInfo':
      return 'Your dependents'
    case 'PersonalTaxRefundMethod':
      return 'Refund preference'
    case 'BusinessInfo':
      return 'Business details'
    case 'BusinessTaxRefundMethod':
      return 'Business refund preference'
    case 'Notes':
      return 'Notes'
    case 'Document':
      return vm.item.name
    default:
      return 'Unknown item'
  }
}

function getSupportingItemTestId(vm: SupportingItemViewModel): string {
  switch (vm.type) {
    case 'PersonalInfo':
      return 'info-gathering-nav-item-personal-info'
    case 'SpousalInfo':
      return 'info-gathering-nav-item-spousal-info'
    case 'DependentsInfo':
      return 'info-gathering-nav-item-dependents-info'
    case 'PersonalTaxRefundMethod':
      return 'info-gathering-nav-item-personal-refund-method'
    case 'BusinessInfo':
      return 'info-gathering-nav-item-business-info'
    case 'BusinessTaxRefundMethod':
      return 'info-gathering-nav-item-business-refund-method'
    case 'Notes':
      return 'info-gathering-nav-item-notes'
    case 'Document':
      return `info-gathering-nav-item-document-${vm.item.input.documentTypeShortId}`
    default:
      return 'info-gathering-nav-item-unknown'
  }
}
